import { useQuery, useMutation } from "@apollo/client/react";
import {
  GetEmailCampaignsDocument,
  GetEmailCampaignDocument,
  CreateEmailCampaignsDocument,
  UpdateEmailCampaignsDocument,
  DeleteEmailCampaignsDocument,
  type GetEmailCampaignsQuery,
  type GetEmailCampaignQuery,
  type CreateEmailCampaignsMutation,
  type UpdateEmailCampaignsMutation,
  type DeleteEmailCampaignsMutation,
  type Email_Campaigns,
  type Email_CampaignsUpdateInput,
} from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import { serializeGraphqlInput } from "@/lib/graphql/serialize-input";
// Writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

/**
 * Email campaigns entity hooks — modeled on `social-media-posts-graphql.ts`.
 *
 * Apollo + usePaginatedCollection (cache-and-network) + serializeGraphqlInput.
 * The EmailCampaignFields fragment selects every displayed column, so the
 * codegen result node carries fragment refs — cast the resolved node to
 * `Email_Campaigns` at the read boundary (same pattern as social posts).
 *
 * `stats` is a JSON column; `serializeGraphqlInput("email_campaigns", …)`
 * handles the JSON scalar serialization required for pg_graphql mutations.
 */

/** Drop Apollo cache metadata (`__typename`, fragment-ref keys) before a mutation. */
function cleanEmailCampaignInput(
  input: Partial<Email_Campaigns>
): Email_CampaignsUpdateInput {
  const entries = Object.entries(input).filter(
    ([key]) => !key.startsWith("__") && key !== "id" && key !== "nodeId"
  );
  return Object.fromEntries(entries);
}

export const useEmailCampaignHooks = (): EntityHooks<Email_Campaigns> => ({
  queryDocument: GetEmailCampaignsDocument,

  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetEmailCampaignsDocument,
      params,
      (data: GetEmailCampaignsQuery) => data.email_campaignsCollection,
      (node) => node as unknown as Email_Campaigns
    ),

  useInfinite: (_params?: Record<string, unknown>) => ({
    data: undefined,
    isLoading: false,
    error: null,
    refetch: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  }),

  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetEmailCampaignQuery>(GetEmailCampaignDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || (options && options.enabled === false),
    });
    const node = result.data?.email_campaignsCollection?.edges?.[0]?.node;
    return {
      data: node ? (node as unknown as Email_Campaigns) : undefined,
      isLoading: result.loading,
      error: result.error || null,
      refetch: result.refetch,
    };
  },

  useCreate: () => {
    const [mutate, result] = useMutation<CreateEmailCampaignsMutation>(
      CreateEmailCampaignsDocument
    );
    return {
      mutateAsync: async (input: Partial<Email_Campaigns>) => {
        const cleanInput = serializeGraphqlInput(
          "email_campaigns",
          cleanEmailCampaignInput(input)
        );
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the email campaign.");
        const created = data?.insertIntoemail_campaignsCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the email campaign.");
        return created as unknown as Email_Campaigns;
      },
      isPending: result.loading,
    };
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateEmailCampaignsMutation>(
      UpdateEmailCampaignsDocument
    );

    return {
      mutateAsync: async ({ id, ...input }: { id: string } & Partial<Email_Campaigns>) => {
        const cleanInput = serializeGraphqlInput(
          "email_campaigns",
          cleanEmailCampaignInput(input)
        );
        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the email campaign.");

        const updated = data?.updateemail_campaignsCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the email campaign.");
        return updated as unknown as Email_Campaigns;
      },
      isPending: result.loading,
    };
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteEmailCampaignsMutation>(
      DeleteEmailCampaignsDocument
    );
    return {
      mutateAsync: async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the email campaign.");
        const deletedId = data?.deleteFromemail_campaignsCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the email campaign.");
        return deletedId;
      },
      isPending: result.loading,
    };
  },
});
